/**
 * A failed load, principal-aware (H §4.5): the message from `describeApiError`, and a Retry
 * that stays disabled until a `429`'s `Retry-After` has elapsed. Remount (clear the error)
 * to reset the countdown.
 *
 * The countdown is announced twice, not every second (TMM-B3, TMMRSL-0068, 0070): the ticking text on
 * the button is `aria-hidden` (visual only). At the start, the Alert's own `role="alert"` announces
 * the message together with one visually hidden, static "Retry (N s)" sentence holding the initial
 * wait, which never ticks. At "ready", a visually hidden polite `role="status"` region says "You can
 * retry now." It is mounted with the Alert but empty until the wait ends, so it fills seconds after
 * it is inserted (a region inserted already filled is often not announced).
 */
import { useEffect, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/errors';
import { ErrorReference } from './ActionErrorAlert';
import { describeApiError } from './useApiErrorHandler';
import { visuallyHidden } from './visuallyHidden';

export interface ApiErrorStateProps {
  error: unknown;
  onRetry: () => void;
  /** Translated noun phrase describing the failed load (TMM-B3). */
  what?: string;
}

export function ApiErrorState({ error, onRetry, what }: ApiErrorStateProps) {
  const { t } = useTranslation();
  const { message, retryAfterSeconds } = describeApiError(error, t);
  const waitSeconds = retryAfterSeconds ?? 0;
  const [remaining, setRemaining] = useState(waitSeconds);

  useEffect(() => {
    if (remaining <= 0) return undefined;
    const id = setTimeout(() => setRemaining((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining]);

  return (
    <Alert
      severity="error"
      action={
        <Button
          color="inherit"
          size="small"
          onClick={onRetry}
          disabled={remaining > 0}
          aria-label={t('common.retry')}
        >
          {remaining > 0 ? (
            <span aria-hidden="true">{t('errors.retryIn', { seconds: remaining })}</span>
          ) : (
            t('common.retry')
          )}
        </Button>
      }
    >
      {what ? <span>{t('errors.loadWhat', { what })} </span> : null}
      {message}
      <ErrorReference requestId={error instanceof ApiError ? error.requestId : undefined} />
      {waitSeconds > 0 ? (
        <>
          {/* Static: the initial wait, announced with the Alert; it never ticks. */}
          <Box component="span" sx={visuallyHidden}>
            {' '}
            {t('errors.retryIn', { seconds: waitSeconds })}
          </Box>
          <Box component="span" role="status" aria-live="polite" sx={visuallyHidden}>
            {remaining <= 0 ? t('errors.retryNow') : ''}
          </Box>
        </>
      ) : null}
    </Alert>
  );
}
